import React, { Dispatch, SetStateAction } from "react";
import { formatDate, formatTime } from "../../../../../../../../Utilits/utilits";
import { useNoAuthHooks } from "../../../../../../../../Hooks/hooks";
import { IOrderResponse } from "../../../../../../../../Interfaces/responseTypes";
import { CurrentOrdersContentTypes, IReserveOrderData, OrderTypes } from "../../../../../../../../Interfaces/componentTypes";
import { OrderStatus } from "../../../../../../../../Interfaces/formValue-interfaces";
import useLanguagesHooks from "../../../../../../../../Components/Languages/setLanguage-hooks";
import { ArrowLeftIcon } from "../../../../../../../../Components/IconComponents";
import { OrderStatusBadge } from "../OrderStatus/statusBadge";
import useCompanyOrderItem from "./companyOrderItem-hook";
import { PrintIcon } from "../../../../../../../../Assets/Icons";
import styles from "./orderItem.module.scss";

export interface ICompanyOrderItemProps {
  order: IOrderResponse;
  onBack: () => void;
  setContentType: Dispatch<SetStateAction<CurrentOrdersContentTypes>>,
  onOpenStatusDialog: (
    order: IOrderResponse,
    setLocalStatus?: Dispatch<SetStateAction<OrderStatus>>,
    current_status?: OrderStatus
  ) => void,
  onOpenPrintInvoceDialog: (order: IOrderResponse) => void,
  currencySymbol: string
}

const OrderItem: React.FC<ICompanyOrderItemProps> = (props) => {
  const { order, currencySymbol, onBack, onOpenStatusDialog, onOpenPrintInvoceDialog } = props;
  const { t } = useLanguagesHooks();
  const { baseURL, mobileScreen, largeScreen } = useNoAuthHooks();
  const {
    typeLabel,
    localStatus,
    orderParams,
    orderData,
    dineInLocationLabel,
    groupedCart,
    onAcceptOrder,
    onRejectOrder,
    setLocalStatus,
    getLocalizedTitle
  } = useCompanyOrderItem(order, currencySymbol);
  
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div onClick={onBack} className={styles.backBtn}>
          <ArrowLeftIcon color='var(--primary-hover)' />
        </div>
        <div className={styles.headerTitleBox}>
          <p className={styles.orderType}>
            {typeLabel} #<span className={styles.orderId}>{order.id}</span>
          </p>
          <p className={styles.orderDate}>
            {formatDate(order.created_at)}
          </p>
        </div>
        <div className={styles.headerStatus}>
          {
            !mobileScreen
            &&
            <OrderStatusBadge
              status={localStatus}
              onClick={() => onOpenStatusDialog(order, setLocalStatus, localStatus)}
            />
          }

        </div>
      </div>
      <div className={styles.orderItem}>
        <div className={styles.infoBox}>
          <div className={styles.info}>
            {orderParams.date && (
              <p>
                <strong>{t("Visitor.My_Order_Card.Date")}</strong>
                {formatDate(orderParams.date, false)}{orderParams.time ? ` ${formatTime(orderParams.time)}` : ''}
              </p>
            )}
            {orderParams.address && (
              <p>
                <strong>{t("Visitor.My_Order_Card.Address")}</strong> {orderParams.address}
              </p>
            )}
            {dineInLocationLabel && (
              <p>
                <strong>{t("My_Business.Current_Orders.User_Location")}</strong>{' '}
                {dineInLocationLabel}
              </p>
            )}
            {
              orderParams.persons_count
              &&
              <p>
                <strong>{t('Visitor.Reserve.Persons')}:</strong> {orderParams.persons_count}
              </p>
            }
            {orderParams.phone_number && (
              <p>
                <strong>{t("Visitor.My_Order_Card.Phone")}</strong> {orderParams.phone_number}
              </p>
            )}
            {(order.order_type === OrderTypes.DELIVERY || order.order_type === OrderTypes.DINE_IN) && (orderParams.cash || orderParams.card) && (
              <p>
                <strong>{t("Visitor.My_Order_Card.Payment")}</strong>{" "}
                {orderParams.card ? t("Visitor.My_Order_Card.Card") : t("Visitor.My_Order_Card.Cash")}
              </p>
            )}
            {orderParams.notes && (
              <p>
                <strong>{t("Visitor.My_Order_Card.Notes")}</strong> {orderParams.notes}
              </p>
            )}
          </div>
          <div className={styles.infoRightSide}>
            {
              largeScreen ?
                <div className={styles.printIcon}
                  onClick={() => onOpenPrintInvoceDialog(order)}>
                  <img src={PrintIcon} alt='printIcon.svg' />
                </div>
                :
                mobileScreen &&
                <OrderStatusBadge
                  status={localStatus}
                  onClick={() => onOpenStatusDialog(order, setLocalStatus, localStatus)}
                />
            }
          </div>
        </div>
        {/* Cart */}
        {
          orderData.cart.length > 0
          &&
          <div className={styles.cart}>
            {Object.entries(groupedCart).map(([categoryName, items], index) => (
              <div key={index} className={styles.categoryBlock}>
                <h4 className={styles.categoryHeader}>{categoryName}</h4>
                {items.map((item) => (
                  <div className={styles.cartBox}>
                    <div key={item.id} className={styles.cartItem}>
                      <div className={styles.itemTitleBox}>
                        <img src={`${baseURL}${item.image}`} alt={`${item.image}.image`} />
                        <p>
                          {getLocalizedTitle(item)}
                        </p>
                      </div>
                      {item.option.length > 0 && (
                        <ul>
                          {item.option.map((opt, i) => (
                            <li key={i} className={styles.optionCount}>
                              {opt.count} × {opt.price} {currencySymbol}
                            </li>
                          ))}
                        </ul>
                      )}

                    </div>
                    {item.note && (
                      <div className={styles.noteBox}>
                        <p>
                          <strong>{t("Visitor.My_Order_Card.Notes")} </strong> {item.note}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        }
        {/* Total */}
        {
          orderData.cart.length > 0
          &&
          <div className={styles.total}>
            {"delivery_cost" in orderData && (
              <p>
                <strong>{t("Visitor.My_Order_Card.Delivery_Fee")} </strong> {orderData.delivery_cost} {currencySymbol}
              </p>
            )}
            {"packageFee" in orderData && (
              <p>
                <strong>{t("Visitor.Delivery.Package_Cost")}: </strong> {orderData.packageFee} {currencySymbol}
              </p>
            )}
            {
              "service_fee" in orderData && (
                <p>
                  <strong>{t('Visitor.Service_Fee')}: </strong> {(orderData as IReserveOrderData).service_fee} %
                </p>
              )
            }
            <p className={styles.totalPrice}>
              <strong >{t("Visitor.Delivery.Total")}: </strong> {orderData.total_cost} {currencySymbol}
            </p>
          </div>
        }
      </div>

      <div className={styles.buttonRow}>
        {
          localStatus === OrderStatus.NEW
            ?
            <>
              <div
                className={styles.acceptButton}
                onClick={onAcceptOrder}
              >
                {t('Buttons.Accept_Order')}
              </div>
              <div
                className={styles.rejectButton}
                onClick={onRejectOrder}
              >
                {t('Buttons.Reject_Order')}
              </div>
            </>
            :
            <div
              className={styles.handleButton}
              onClick={() => onOpenStatusDialog(order, setLocalStatus, localStatus)}
            >
              {t('Buttons.Manage_Order')}
            </div>
        }

      </div>
    </div>
  );
};

export default OrderItem;
